import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Package, FileText, Users, LogOut, Languages, Settings, Library, Contact, Factory, ClipboardList, LifeBuoy, FileSpreadsheet, Trash2, ShoppingCart, ChevronDown, Check, Plus, Menu, KeyRound, Tag, Store, PanelLeftClose, PanelLeftOpen, Star, Sparkles, Clock, User, Barcode, ShieldCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { useQuote } from "@/context/QuoteContext";
import { MobileTabBar } from "@/components/MobileTabBar";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { buildNavItems, buildAdminItems } from "@/lib/navItems";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";

function HeaderQuoteBadge() {
  // 2026-02-26 · Cart-style dropdown per user spec (screenshot). Shows the
  // latest 10 quotes, a checkmark on the active one, a "+ New Quote" entry,
  // and an "All Quotes" link. Replaces the old read-only badge.
  const { t } = useLanguage();
  const { activeQuote, selectQuote, count } = useQuote();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    api.get("/quotes")
      .then(({ data }) => { if (!cancelled) setQuotes(Array.isArray(data) ? data.slice(0, 10) : []); })
      .catch(() => { if (!cancelled) setQuotes([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  const handlePick = (q) => {
    selectQuote(q);
    setOpen(false);
  };

  const handleNew = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const { data } = await api.post("/quotes", {});
      toast.success(`${t("quote.created")}: ${data.quote_number}`);
      selectQuote(data);
      setOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const label = activeQuote?.quote_number || t("quote.pickQuote");
  // 2026-02-09 · No active quote → highlight the button (red dot + colored
  // border) so users notice they must pick one before "加入报价单" works
  // from product-form / product-list.
  const empty = !activeQuote;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="header-quote-cart-trigger"
          className={`flex items-center gap-2 h-8 px-2.5 rounded-md border text-sm font-medium transition-colors select-none ${empty
            ? "border-rose-400 bg-rose-50 text-rose-700 hover:bg-rose-100 animate-pulse-subtle"
            : "border-border bg-secondary/40 hover:bg-secondary"}`}
          title={empty ? (t("quote.noActiveHint") || "尚未选择活跃报价单") : t("quote.activeQuote")}
        >
          <span className="relative">
            <ShoppingCart size={16} className={empty ? "text-rose-500" : "text-brand"} />
            {activeQuote && count > 0 && (
              <span
                data-testid="header-quote-cart-count"
                className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-brand text-white text-[10px] font-mono flex items-center justify-center"
              >
                {count}
              </span>
            )}
            {empty && (
              <span
                data-testid="header-quote-empty-dot"
                className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-rose-500 ring-2 ring-white animate-pulse"
                aria-label="no active quote"
              />
            )}
          </span>
          <span className="max-w-[92px] md:max-w-[160px] truncate font-mono">{label}</span>
          <ChevronDown size={13} className={empty ? "text-rose-500" : "text-muted-foreground"} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-72 max-h-[70vh] overflow-y-auto"
        data-testid="header-quote-dropdown"
      >
        <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-mono">
          {t("quote.switchLatest")}
        </DropdownMenuLabel>
        {loading && (
          <div className="px-2 py-3 text-xs text-muted-foreground text-center">…</div>
        )}
        {!loading && quotes.length === 0 && (
          <div
            data-testid="header-quote-empty"
            className="px-2 py-3 text-xs text-muted-foreground text-center"
          >
            {t("quote.emptyListHint")}
          </div>
        )}
        {!loading && quotes.map((q) => {
          const isActive = activeQuote?.id === q.id;
          const itemCount = Array.isArray(q.items) ? q.items.length : 0;
          return (
            <DropdownMenuItem
              key={q.id}
              onSelect={() => handlePick(q)}
              data-testid={`header-quote-item-${q.quote_number}`}
              className="flex items-center gap-2 py-2 cursor-pointer"
            >
              <span className="w-4 flex items-center justify-center text-brand">
                {isActive ? <Check size={14} /> : null}
              </span>
              <span className="flex-1 truncate font-mono text-[13px]">{q.quote_number}</span>
              <span className="font-mono text-xs text-muted-foreground">{itemCount}</span>
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={(e) => { e.preventDefault(); handleNew(); }}
          disabled={creating}
          data-testid="header-quote-new"
          className="flex items-center gap-2 py-2 cursor-pointer text-brand"
        >
          <Plus size={14} />
          <span className="flex-1">{t("quote.new")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => { setOpen(false); navigate("/quotes"); }}
          data-testid="header-quote-all"
          className="flex items-center gap-2 py-2 cursor-pointer"
        >
          <Menu size={14} />
          <span className="flex-1">{t("quote.allQuotes")}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// -----------------------------------------------------------------------------
// ProductSubNav — 2026-06 · 「产品」二级菜单: 全部产品 / 我的收藏 / 只看新品 /
// 最近查看 / 我的产品. Each entry just drives the product list's URL filters,
// so the filtering logic itself stays in ProductsList.
// -----------------------------------------------------------------------------
function ProductSubNav({ role, t }) {
  const location = useLocation();
  const [favCount, setFavCount] = useState(0);

  useEffect(() => {
    const load = () => api.get("/products/favorites")
      .then(({ data }) => setFavCount(data?.count ?? 0))
      .catch(() => {});
    load();
    window.addEventListener("favorites-changed", load);
    return () => window.removeEventListener("favorites-changed", load);
  }, []);

  const params = new URLSearchParams(location.search);
  const onProducts = location.pathname === "/";
  const flag = (k) => onProducts && params.get(k) === "1";
  const noneActive = onProducts && !flag("favorites") && !flag("new_only") && !flag("recent") && !flag("mine");

  const showMine = ["admin", "export", "gt_export", "buyer", "sales_manager", "procurement_manager"].includes(role);
  const entries = [
    { key: "all", to: "/?show_all=1", icon: FileText, label: t("products.allProducts"), active: noneActive, testId: "subnav-all-products" },
    { key: "favorites", to: "/?favorites=1", icon: Star, label: t("products.favoritesOnly"), active: flag("favorites"), badge: favCount, testId: "subnav-favorites" },
    { key: "new_only", to: "/?new_only=1", icon: Sparkles, label: t("products.newOnly"), active: flag("new_only"), testId: "subnav-new-only" },
    { key: "recent", to: "/?recent=1", icon: Clock, label: t("products.recentlyViewed"), active: flag("recent"), testId: "subnav-recent" },
    showMine ? { key: "mine", to: "/?mine=1", icon: User, label: t("products.mineOnly"), active: flag("mine"), testId: "subnav-mine" } : null,
    // 2026-09 · UPC 查询 — browse/search the UPC codes synced from FileMaker.
    { key: "upc", to: "/upc-codes", icon: Barcode, label: t("upc.pageTitle"),
      active: location.pathname === "/upc-codes", testId: "subnav-upc-codes" },
  ].filter(Boolean);

  return (
    <div className="mt-0.5 ml-3 pl-1.5 border-l border-border space-y-0.5" data-testid="product-subnav">
      {entries.map((e) => (
        <NavLink
          key={e.key}
          to={e.to}
          data-testid={e.testId}
          className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-[13px] font-medium transition-colors ${
            e.active ? "bg-brand/10 text-brand" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
          }`}
        >
          <e.icon size={14} className="shrink-0" />
          <span className="truncate">{e.label}</span>
          {e.badge > 0 && (
            <span className="ml-auto shrink-0 px-1.5 rounded-full bg-amber-500/15 text-amber-700 text-[10px] font-semibold leading-4">
              {e.badge}
            </span>
          )}
        </NavLink>
      ))}
    </div>
  );
}

export function Layout() {
  const { user, logout } = useAuth();
  const { t, lang, toggleLang } = useLanguage();
  const navigate = useNavigate();
  // 2026-02-26 · Self-service change-password dialog wired to the top-right
  // user dropdown menu (replaces the email-based forgot-password flow in day-to-day use).
  const [changePwOpen, setChangePwOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const role = user?.role;
  // 2026-02-09 · Nav registry is now shared with the mobile drawer /
  // bottom tab bar via ``/lib/navItems`` so sales_manager and other roles
  // no longer see an empty menu on iPhone Safari.
  const navItems = buildNavItems(role, t);
  const adminItems = buildAdminItems(role, t);
  // 2026-02-09 · Hamburger sheet state for mobile.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [sidebarHidden, setSidebarHidden] = useState(() => localStorage.getItem("sidebarHidden") === "1");
  // 2026-06 · 「产品」二级菜单展开状态（默认展开，用户可折叠）。
  const [productNavOpen, setProductNavOpen] = useState(() => localStorage.getItem("productNavOpen") !== "0");

  useEffect(() => {
    localStorage.setItem("productNavOpen", productNavOpen ? "1" : "0");
  }, [productNavOpen]);

  useEffect(() => {
    localStorage.setItem("sidebarHidden", sidebarHidden ? "1" : "0");
  }, [sidebarHidden]);

  // 2026-06 · 顶栏与左侧菜单统一字号（14px），按钮 32px 高保持顶栏 48px。
  const headerBtn = "flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-border text-sm font-medium hover:bg-secondary transition-colors";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="h-12 shrink-0 bg-white border-b border-border flex items-center justify-between px-3 md:px-5 sticky top-0 z-20">
        <div className="flex items-center gap-2 md:gap-2.5">
          {/* 2026-02-09 · Mobile hamburger — opens a full nav drawer so
           * users (esp. sales_manager) get every menu item on iPhone Safari,
           * matching the desktop sidebar + admin header buttons. */}
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            data-testid="mobile-nav-trigger"
            className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-md border border-border hover:bg-secondary transition-colors"
            aria-label={t("nav.menu") || "Menu"}
          >
            <Menu size={18} />
          </button>
          <div className="w-7 h-7 rounded-md border border-border flex items-center justify-center">
            <Library size={14} className="text-brand" />
          </div>
          <div>
            <span className="font-heading font-extrabold text-[15px] tracking-tight leading-none hidden sm:block whitespace-nowrap">{t("app.title")}</span>
            <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground leading-none hidden md:block">{t("app.caption")}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-testid="header-user-trigger"
                className="font-mono text-xs text-muted-foreground mr-1 hidden md:flex items-center gap-1 h-8 px-2 rounded-md hover:bg-secondary hover:text-foreground transition-colors"
                title={t("nav.userMenu")}
              >
                <span data-testid="current-user-name">
                  {user?.email}{role ? ` · ${t(`users.role_${role}`)}` : ""}
                </span>
                <ChevronDown size={12} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52" data-testid="header-user-menu">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-mono">
                {user?.name || user?.email}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => setChangePwOpen(true)}
                data-testid="menu-change-password"
                className="cursor-pointer gap-2"
              >
                <KeyRound size={14} /> {t("nav.changePassword")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {["admin", "export", "sales_manager"].includes(role) && <HeaderQuoteBadge />}
          {user?.role === "admin" && (
            <>
              <NavLink to="/system-templates" data-testid="nav-system-templates" className={`${headerBtn} hidden md:flex`}>
                <FileSpreadsheet size={15} /> <span className="hidden lg:inline">{t("nav.systemTemplates")}</span>
              </NavLink>
              <NavLink to="/users" data-testid="nav-users" className={`${headerBtn} hidden md:flex`}>
                <Users size={15} /> <span className="hidden lg:inline">{t("nav.users")}</span>
              </NavLink>
              <NavLink to="/backup-health" data-testid="nav-backup-health" className={`${headerBtn} hidden md:flex`} title="备份与健康 / Backup & Health">
                <ShieldCheck size={15} /> <span className="hidden lg:inline">备份</span>
              </NavLink>
              <NavLink to="/settings" data-testid="nav-settings" className={`${headerBtn} hidden md:flex`}>
                <Settings size={15} /> <span className="hidden lg:inline">{t("nav.settings")}</span>
              </NavLink>
            </>
          )}
          <button onClick={toggleLang} data-testid="lang-toggle" className={headerBtn}>
            <Languages size={14} />
            <span className="font-mono text-xs">{lang === "en" ? "中文" : "EN"}</span>
          </button>
          <button onClick={handleLogout} data-testid="logout-button" className={headerBtn}>
            <LogOut size={15} /> <span className="hidden lg:inline">{t("nav.logout")}</span>
          </button>
        </div>
      </header>

      <div className="flex-1 flex min-h-0">
        {/* 2026-06 · Sidebar can be hidden entirely (user spec); the state is
            remembered in localStorage and a floating handle brings it back. */}
        {sidebarHidden ? (
          <button
            onClick={() => setSidebarHidden(false)}
            title={t("nav.showSidebar")}
            data-testid="sidebar-show-button"
            className="hidden md:flex items-center justify-center h-9 w-6 mt-3 ml-1 shrink-0 self-start rounded-r-md border border-l-0 border-border bg-white text-muted-foreground hover:text-brand hover:bg-secondary transition-colors"
          >
            <PanelLeftOpen size={15} />
          </button>
        ) : (
          <aside className="hidden md:block w-44 shrink-0 bg-white border-r border-border py-3 px-2 space-y-0.5" data-testid="sidebar">
            <button
              onClick={() => setSidebarHidden(true)}
              title={t("nav.hideSidebar")}
              data-testid="sidebar-hide-button"
              className="w-full flex items-center justify-end px-2 py-1 mb-1 rounded-md text-muted-foreground hover:text-brand hover:bg-secondary transition-colors"
            >
              <PanelLeftClose size={15} />
            </button>
            {navItems.map((item) => (
              <div key={item.to}>
                {item.to === "/" ? (
                  <div className="relative">
                    <NavLink
                      to={item.to}
                      data-testid={item.testId}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 pl-3 pr-8 py-2 rounded-md text-sm font-medium transition-colors ${
                          isActive ? "bg-brand text-white" : "text-foreground hover:bg-secondary"
                        }`
                      }
                    >
                      <item.icon size={16} />
                      {item.label}
                    </NavLink>
                    <button
                      type="button"
                      onClick={() => setProductNavOpen((v) => !v)}
                      data-testid="product-subnav-toggle"
                      title={item.label}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded text-current opacity-70 hover:opacity-100"
                    >
                      <ChevronDown size={14} className={`transition-transform ${productNavOpen ? "" : "-rotate-90"}`} />
                    </button>
                  </div>
                ) : (
                  <NavLink
                    to={item.to}
                    data-testid={item.testId}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        isActive ? "bg-brand text-white" : "text-foreground hover:bg-secondary"
                      }`
                    }
                  >
                    <item.icon size={16} />
                    {item.label}
                  </NavLink>
                )}
                {/* 2026-06 · 「产品」二级菜单（用户规格）：把原来顶部工具栏的
                    个人视图按钮移到这里，避免重复占用页面高度。 */}
                {item.to === "/" && productNavOpen && <ProductSubNav role={role} t={t} />}
              </div>
            ))}
          </aside>
        )}
        <main className="flex-1 p-4 pb-24 md:p-6 md:pb-6 overflow-y-auto min-w-0">
          <Outlet />
        </main>
      </div>
      <MobileTabBar onOpenMore={() => setMobileNavOpen(true)} />
      {/* 2026-02-09 · Mobile navigation drawer — mirrors the full desktop
       * sidebar + admin header items so every role can reach every module
       * on iPhone Safari. */}
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent side="left" className="w-72 p-0 flex flex-col" data-testid="mobile-nav-drawer">
          <SheetHeader className="px-4 py-3 border-b border-border shrink-0">
            <SheetTitle className="flex items-center gap-2 text-sm">
              <Library size={15} className="text-brand" />
              <span className="font-heading">{t("app.title")}</span>
            </SheetTitle>
            {user?.email && (
              <div className="text-[11px] font-mono text-muted-foreground text-left mt-1" data-testid="mobile-nav-user">
                {user.email}{role ? ` · ${t(`users.role_${role}`)}` : ""}
              </div>
            )}
          </SheetHeader>
          <nav className="flex-1 overflow-y-auto p-2 space-y-0.5">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-${item.testId}`}
                onClick={() => setMobileNavOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive ? "bg-brand text-white" : "text-foreground hover:bg-secondary"
                  }`
                }
              >
                <item.icon size={16} />
                {item.label}
              </NavLink>
            ))}
            {adminItems.length > 0 && (
              <>
                <div className="pt-3 pb-1 px-3 text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-mono">
                  {t("nav.admin") || "Admin"}
                </div>
                {adminItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    data-testid={`mobile-${item.testId}`}
                    onClick={() => setMobileNavOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                        isActive ? "bg-brand text-white" : "text-foreground hover:bg-secondary"
                      }`
                    }
                  >
                    <item.icon size={16} />
                    {item.label}
                  </NavLink>
                ))}
              </>
            )}
          </nav>
          <div className="border-t border-border p-2 space-y-0.5 shrink-0">
            <button
              type="button"
              onClick={() => { setMobileNavOpen(false); setChangePwOpen(true); }}
              data-testid="mobile-nav-change-password"
              className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm font-medium text-foreground hover:bg-secondary transition-colors"
            >
              <KeyRound size={16} /> {t("nav.changePassword")}
            </button>
            <button
              type="button"
              onClick={() => { setMobileNavOpen(false); handleLogout(); }}
              data-testid="mobile-nav-logout"
              className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm font-medium text-foreground hover:bg-secondary transition-colors"
            >
              <LogOut size={16} /> {t("nav.logout")}
            </button>
          </div>
        </SheetContent>
      </Sheet>
      <ChangePasswordDialog open={changePwOpen} onOpenChange={setChangePwOpen} />
    </div>
  );
}

// -----------------------------------------------------------------------------
// ChangePasswordDialog — 2026-02-26 · Simple self-service password change.
// Requires the current password + a new one (min 6 chars, must differ).
// -----------------------------------------------------------------------------
function ChangePasswordDialog({ open, onOpenChange }) {
  const { t } = useLanguage();
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setOldPw(""); setNewPw(""); setConfirmPw(""); setBusy(false); };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!oldPw || !newPw || !confirmPw) return;
    if (newPw.length < 6) { toast.error(t("changePw.tooShort")); return; }
    if (newPw !== confirmPw) { toast.error(t("changePw.mismatch")); return; }
    if (newPw === oldPw) { toast.error(t("changePw.sameAsOld")); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { old_password: oldPw, new_password: newPw });
      toast.success(t("changePw.success"));
      onOpenChange(false);
      reset();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-sm" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-base flex items-center gap-2">
            <KeyRound size={16} /> {t("nav.changePassword")}
          </DialogTitle>
          <DialogDescription className="text-xs">{t("changePw.hint")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="cp-old">{t("changePw.oldPassword")}</Label>
            <Input id="cp-old" type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} autoFocus data-testid="cp-old-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cp-new">{t("changePw.newPassword")}</Label>
            <Input id="cp-new" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="cp-new-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cp-confirm">{t("changePw.confirmPassword")}</Label>
            <Input id="cp-confirm" type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} data-testid="cp-confirm-input" />
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-md">{t("common.cancel")}</Button>
            <Button type="submit" disabled={busy || !oldPw || !newPw || !confirmPw} data-testid="cp-submit-button" className="rounded-md bg-brand hover:bg-brand-hover">
              {busy ? "..." : t("changePw.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
